import type { PermissionKey } from "./permission-keys.js";
import * as Exact from "./decimal.js";

// Fixed assets (phase 10; rules T-1 to T-8, provisional: docs/proposed-financial-rules.md, section 11). The schedule
// and the purchase line values are worked out here so the server and the screens agree to the cent.

export interface AccountRefLite { id: string; code: string; nameEn: string; nameAr: string }
export interface NamedRef { id: string; nameEn: string; nameAr: string }

export interface AssetKindInput { nameAr: string; nameEn: string; months: number; assetAccountId: string; accumulatedAccountId: string; expenseAccountId: string }
export interface AssetKindDto extends AssetKindInput {
  id: string; assetAccount: AccountRefLite; accumulatedAccount: AccountRefLite; expenseAccount: AccountRefLite;
  assetsCount: number;
  /** An asset of the kind has a deported period: the period and the accounts can no longer change (T-6). */
  locked: boolean;
  createdAt: string; updatedAt: string;
}

export interface AssetInput { title: string; kindId: string; amount: string | number; startOn: string; depreciates: boolean }
export interface AssetDto {
  id: string; title: string; kind: NamedRef & { months: number }; amount: string; startOn: string; depreciates: boolean; monthly: string;
  periods: number; deportedPeriods: number; accumulated: string; bookValue: string; lastDeportedTo: string | null;
  invoice: { id: string; code: string } | null; cancelled: boolean;
  /** A deported period, or the asset came from a purchase (corrected by reversing the purchase): not editable (T-6). */
  locked: boolean;
}
export interface AssetsDto { assets: AssetDto[]; total: { amount: string; monthly: string; accumulated: string; bookValue: string } }

export interface DepreciationRowDto {
  id: string; asset: { id: string; title: string; cancelled: boolean }; periodNo: number; fromOn: string; toOn: string; amount: string;
  deported: boolean; entry: { id: string; number: string } | null;
  /** Ended and not deported, and the asset's earlier periods are all deported: it can be deported now (T-4). */
  due: boolean;
  /** The asset's latest deported period: the only one that can be undeported (T-5). */
  undeportable: boolean;
  accumulatedAccount: AccountRefLite; expenseAccount: AccountRefLite;
}
export interface DepreciationsDto { rows: DepreciationRowDto[]; today: string; dueCount: number; dueAmount: string; total: string; deported: string }
export interface DeportResult { deported: number; amount: string }

export interface AssetInvoiceLineInput { statement: string; kindId: string; qty: string | number; price: string | number; taxTypeId: string }
export interface AssetInvoiceInput {
  invoiceAt: string; warehouseId: string; partyId: string; treasuryId: string | null; paid: string | number;
  reference: string; note: string; lines: AssetInvoiceLineInput[];
}
export interface AssetInvoiceLineDto { lineNo: number; statement: string; kind: NamedRef; qty: string; price: string; taxType: NamedRef; taxRate: string; net: string; tax: string; total: string }
export interface AssetInvoiceListDto {
  id: string; code: string; invoiceAt: string; warehouse: NamedRef; party: { id: string; name: string }; subtotal: string; vat: string; grand: string; paid: string;
  remaining: string; reversedAt: string | null; createdBy: string;
}
export interface AssetInvoiceDto extends AssetInvoiceListDto {
  treasury: NamedRef | null; reference: string; note: string; lines: AssetInvoiceLineDto[];
  assets: { id: string; title: string; amount: string; monthly: string; cancelled: boolean }[];
  journalEntry: { id: string; number: string } | null; reversalEntry: { id: string; number: string } | null; reversalAt: string | null;
  /** No asset of it has a deported period (T-3). */
  reversible: boolean;
}
export interface AssetInvoicesDto { invoices: AssetInvoiceListDto[]; total: { subtotal: string; vat: string; grand: string; paid: string; remaining: string } }
export interface AssetOptionsDto {
  kinds: (NamedRef & { months: number })[];
  warehouses: (NamedRef & { branchId: string })[];
  treasuries: (NamedRef & { branchId: string; accountId: string | null })[];
  suppliers: { id: string; name: string; accountId: string | null }[];
  taxTypes: (NamedRef & { rate: string })[];
}

export const ASSET_INVOICE_PREFIX = "AP-";
export const assetInvoiceCode = (number: string | number) => `${ASSET_INVOICE_PREFIX}${number}`;

/** Keys of the audited route table: view, Add, Update, Delete per screen. */
export const ASSET_KEYS = {
  kinds: { view: "assetKinds", add: "assetKindAdd", update: "assetKindUpdate", del: "assetKindDelete" },
  assets: { view: "assets", add: "assetAdd", update: "assetUpdate", del: "assetDelete" },
  invoices: { view: "assetInvoices", add: "assetInvoiceAdd", update: "assetInvoiceUpdate", del: "assetInvoiceDelete" },
  depreciation: { view: "assetDepreciations", deport: "assetDepreciationDeport", undeport: "assetDepreciationCancelDeport" }
} as const satisfies Record<string, Record<string, PermissionKey>>;

export interface SchedulePeriod { periodNo: number; fromOn: string; toOn: string; amount: string }

const pad = (n: number) => String(n).padStart(2, "0");
const lastDay = (y: number, m: number) => new Date(Date.UTC(y, m, 0)).getUTCDate();

/**
 * T-2: the whole schedule of an asset. One period per month from the month it starts in, `months` periods; each is
 * amount ÷ months rounded to two decimals half away from zero, and the last takes what is left, so the periods add up
 * to the amount exactly. Null when a period would come to zero (the amount is too small for the period).
 */
export function depreciationSchedule(amount: string, months: number, startOn: string): { monthly: string; periods: SchedulePeriod[] } | null {
  const a = Exact.dec(amount);
  const monthly = Exact.div(a, Exact.dec(String(months)), 2);
  const last = Exact.sub(a, Exact.mul(monthly, Exact.dec(String(months - 1))));
  if (monthly.n <= 0n || last.n <= 0n) return null;
  let [y, m] = startOn.slice(0, 7).split("-").map(Number) as [number, number];
  const periods: SchedulePeriod[] = [];
  for (let i = 1; i <= months; i++) {
    periods.push({ periodNo: i, fromOn: `${y}-${pad(m)}-01`, toOn: `${y}-${pad(m)}-${pad(lastDay(y, m))}`, amount: Exact.fmt(i === months ? last : monthly, 2) });
    m++;
    if (m > 12) { m = 1; y++; }
  }
  return { monthly: Exact.fmt(monthly, 2), periods };
}

/** T-3: a purchase line's value before VAT (quantity × price), its VAT at the tax type's rate, and its total, to the cent. */
export function assetLineCalc(qty: string, price: string, rate: string): { net: string; tax: string; total: string } {
  const net = Exact.roundHalfAway(Exact.mul(Exact.dec(qty), Exact.dec(price)), 2);
  const tax = Exact.percentOf(net, Exact.dec(rate));
  return { net: Exact.fmt(net, 2), tax: Exact.fmt(tax, 2), total: Exact.fmt(Exact.add(net, tax), 2) };
}

// ---------------------------------------------------------------- reports (T-8)

export interface AssetReportRow {
  id: string; title: string; kind: NamedRef; at: string; amount: string; monthly: string; startOn: string;
  accumulated: string; bookValue: string; lastDeportedTo: string | null; invoice: { id: string; code: string } | null;
}
/** Assets on the books as at `to` (cancelled ones left out), accumulated depreciation from the periods deported that end by then. */
export interface AssetReportDto { from: string | null; to: string | null; rows: AssetReportRow[]; amount: string; monthly: string; accumulated: string; bookValue: string }
export interface AssetInvoiceReportRow {
  id: string; code: string; at: string; reversal: boolean; warehouse: NamedRef; party: { id: string; name: string };
  subtotal: string; vat: string; grand: string; paid: string; remaining: string; byName: string | null;
}
export interface AssetInvoiceReportDto { from: string | null; to: string | null; rows: AssetInvoiceReportRow[]; subtotal: string; vat: string; grand: string; paid: string; remaining: string }
export interface AssetInvoiceDetailRow {
  id: string; code: string; at: string; reversal: boolean; warehouse: NamedRef; kind: NamedRef; statement: string; qty: string; price: string; tax: string; total: string; byName: string | null;
}
export interface AssetInvoiceDetailsDto { from: string | null; to: string | null; rows: AssetInvoiceDetailRow[]; tax: string; total: string }
